import { API } from "aws-amplify";
import LocalizedStrings from "react-localization";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useContext, useMemo, useState } from "react";

import ServiceOrderForm from "components/serviceOrders/ServiceOrderForm";
import {
  createServiceOrder,
  createServiceOrderEquipment,
  createServiceOrderMaintenanceRoutine,
  createServiceOrderTool,
  updateServiceOrder,
  updateSpare,
} from "graphql/mutations";
import { LayoutContext } from "layout/context/LayoutContext";
import { serviceOrderStatus } from "config/constants";
import LoaderFullScreen from "components/utils/LoaderFullScreen";
import moment from "moment";
import ConfirmModal from "components/utils/ConfirmModal";

const ServiceOrderCreateForm = ({ onSuccess }) => {
  const { me } = useContext(LayoutContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [modalConfirm, setModalConfirm] = useState({
    visible: false,
    values: null,
  });
  const serviceOrder = useMemo(
    () => ({
      description: "",
      billDate: "",
      billValue: "",
      billCurrency: "",
      companyID: me.companyID ?? "",
      branchID: "",
      costCenterID: "",
      color: "",
      observations: "",
      title: "",
      userExecutor: "",
      start: "",
      end: "",
      tools: [],
      spares: [],
      maintenanceRoutines: [],
      auxExecutors: [],
      geolocation: false,
      equipments: [],
      equipmentRoutines: [],
      equipmentSpares: [],
      photoRequired: false,
    }),
    [me.companyID]
  );

  async function onCreateServiceOrder(valuesParam) {
    const {
      description,
      billDate,
      billValue,
      billCurrency,
      companyID,
      branchID,
      costCenterID,
      color,
      observations,
      title,
      userExecutor,
      start,
      end,
      tools,
      spares,
      maintenanceRoutines,
      auxExecutors,
      geolocation,
      equipments,
      equipmentRoutines,
      equipmentSpares,
      photoRequired
    } = valuesParam;

    const status =
      start && end ? serviceOrderStatus.scheduled : serviceOrderStatus.created;

    const serviceOrderCreated = (
      await API.graphql({
        query: createServiceOrder.replaceAll("__typename", ""),
        variables: {
          input: {
            code: "temp-code",
            description,
            billDate,
            billValue,
            billCurrency,
            companyID,
            branchID,
            costCenterID,
            color,
            observations,
            title,
            userExecutor: userExecutor || null,
            auxExecutors,
            status,
            start: start || null,
            end: end || null,
            geolocation,
            equipmentRoutines,
            equipmentSpares,
            photoRequired
          },
        },
      })
    ).data.createServiceOrder;

    await API.graphql({
      query: updateServiceOrder.replaceAll("__typename", ""),
      variables: {
        input: {
          id: serviceOrderCreated.id,
          code: serviceOrderCreated.id.substring(0, 6),
        },
      },
    });

    const promises = [];

    promises.push(
      ...tools?.map((toolId) =>
        API.graphql({
          query: createServiceOrderTool.replaceAll("__typename", ""),
          variables: {
            input: {
              serviceOrderId: serviceOrderCreated.id,
              toolId,
            },
          },
        })
      )
    );

    promises.push(
      ...spares?.map((spareId) =>
        API.graphql({
          query: updateSpare.replaceAll("__typename", ""),
          variables: {
            input: {
              id: spareId,
              serviceOrderID: serviceOrderCreated.id,
            },
          },
        })
      )
    );

    promises.push(
      ...maintenanceRoutines?.map((maintenanceRoutineId) =>
        API.graphql({
          query: createServiceOrderMaintenanceRoutine.replaceAll(
            "__typename",
            ""
          ),
          variables: {
            input: {
              serviceOrderId: serviceOrderCreated.id,
              maintenanceRoutineId,
            },
          },
        })
      )
    );

    promises.push(
      ...equipments.reduce((promises, equipment) => {
        promises.push(
          API.graphql({
            query: createServiceOrderEquipment.replaceAll("__typename", ""),
            variables: {
              input: {
                serviceOrderId: serviceOrderCreated.id,
                equipmentId: equipment.id,
              },
            },
          })
        );
        return promises;
      }, [])
    );

    await Promise.all(promises);
  }

  const confirmCreate = () => {
    onSubmit(modalConfirm.values, true);
    setModalConfirm({ visible: false });
  };

  async function onSubmit(valuesParam, force) {
    try {
      const { start, end, serie, times, timeInterval, until } = valuesParam;

      setLoading(true);

      const dates = [];
      if (serie && start && end) {
        const untilDate = moment(until);

        let currentStartDate = moment(start);
        let currentEndDate = moment(end);
        while (currentStartDate.isSameOrBefore(untilDate, "day")) {
          dates.push({
            start: currentStartDate.toDate(),
            end: currentEndDate.toDate(),
          });
          currentStartDate.add(times || 1, timeInterval || "day");
          currentEndDate.add(times || 1, timeInterval || "day");
        }
      }

      if (dates.length > 0) {
        if (!force) {
          setModalConfirm({
            visible: true,
            values: valuesParam,
            quantity: dates.length,
          });
          return;
        }
        for (const date of dates) {
          await onCreateServiceOrder({
            ...valuesParam,
            start: date.start,
            end: date.end,
          });
        }
      } else await onCreateServiceOrder(valuesParam);

      toast.success(strings.successAdded);
      if (onSuccess) onSuccess();
      else navigate("/service-orders");
    } catch (err) {
      toast.error(strings.responses[err]);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <ServiceOrderForm onSubmit={onSubmit} initialValues={serviceOrder} />
      <ConfirmModal
        visible={modalConfirm.visible}
        body={strings.formatString(strings.modal.confirmCreateQuestion, {
          quantity: modalConfirm.quantity,
        })}
        onCancel={() => setModalConfirm({ visible: false })}
        onConfirm={confirmCreate}
      />
      {loading && <LoaderFullScreen />}
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Agregar Orden de Servicio",
    successAdded: "Orden de Servicio agregada con éxito",
    responses: {},
    validations: {},
    modal: {
      confirmCreateQuestion:
        "Se crearán {quantity} ordenes en total ¿Estás seguro de realizar la acción?",
    },
  },
});

export default ServiceOrderCreateForm;
